/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Avatar,
  Dropdown,
  Input,
  Modal,
  Space,
  TextArea,
  Toast,
  Typography,
} from '@coze-arch/coze-design';
import { SpaceType } from '@coze-arch/idl/playground_api';
import { useSpaceStore } from '@coze-foundation/space-store';

export interface ISpaceSwitcherProps {
  /** 当前二级菜单，切换空间时尽量停留在同一菜单 */
  subMenuKey?: string;
}

// [zhaoran:space] 开源版只有一个默认空间且没有创建入口，
// 这里补一个空间切换下拉 + 新建空间弹窗，后端接口见 zhaoran_space_api.go
export const SpaceSwitcher: FC<ISpaceSwitcherProps> = ({ subMenuKey }) => {
  const navigate = useNavigate();

  const currentSpace = useSpaceStore(state => state.space);
  const spaceList = useSpaceStore(state => state.spaceList);
  const createSpace = useSpaceStore(state => state.createSpace);
  const fetchSpaces = useSpaceStore(state => state.fetchSpaces);
  const setSpace = useSpaceStore(state => state.setSpace);

  const [modalVisible, setModalVisible] = useState(false);
  /** [zhaoran:space] 编辑模式：非空表示正在改名/改描述的目标空间 */
  const [editingSpaceId, setEditingSpaceId] = useState<string>('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const goToSpace = (id?: string) => {
    if (!id) {
      return;
    }
    setSpace(id);
    navigate(`/space/${id}/${subMenuKey || 'develop'}`);
  };

  const openCreateModal = () => {
    setEditingSpaceId('');
    setName('');
    setDescription('');
    setModalVisible(true);
  };

  const openEditModal = (space: {
    id?: string;
    name?: string;
    description?: string;
  }) => {
    if (!space.id) {
      return;
    }
    setEditingSpaceId(space.id);
    setName(space.name || '');
    setDescription(space.description || '');
    setModalVisible(true);
  };

  const handleSubmit = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      Toast.warning('请输入工作空间名称');
      return;
    }

    setSubmitting(true);
    try {
      const ret = await createSpace({
        // 后端 SaveSpaceV2 约定：带 space_id 即更新（改名/改描述）
        ...(editingSpaceId ? { space_id: editingSpaceId } : {}),
        name: trimmedName,
        description: description.trim(),
        icon_uri: '',
        space_type: SpaceType.Personal,
      });
      // 强制刷新空间列表，确保新建/改名立刻生效
      await fetchSpaces(true);
      if (!editingSpaceId && ret?.id) {
        goToSpace(ret.id);
      }
      // [zhaoran:space] 改的是当前空间时，同步刷新头部展示的空间名
      if (editingSpaceId && currentSpace?.id === editingSpaceId) {
        setSpace(editingSpaceId);
      }
      setModalVisible(false);
      setEditingSpaceId('');
      setName('');
      setDescription('');
      Toast.success(
        editingSpaceId
          ? `工作空间已改名为「${trimmedName}」`
          : `工作空间「${trimmedName}」创建成功`,
      );
    } catch (e) {
      Toast.error(
        (e as Error).message ||
          (editingSpaceId ? '修改工作空间失败' : '创建工作空间失败'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dropdown
        trigger="hover"
        position="rightBottom"
        render={
          <Dropdown.Menu mode="menu" className="w-[260px]">
            {spaceList.map(space => (
              <Dropdown.Item key={space.id} onClick={() => goToSpace(space.id)}>
                <div className="flex items-center gap-[8px] w-full">
                  <Avatar
                    className="w-[20px] h-[20px] rounded-[4px] shrink-0"
                    src={space.icon_url}
                  />
                  <Typography.Text
                    ellipsis={{ showTooltip: true, rows: 1 }}
                    className="flex-1"
                  >
                    {space.name}
                  </Typography.Text>
                  <span
                    className="coz-fg-secondary shrink-0 hover:coz-fg-primary text-[12px]"
                    onClick={e => {
                      e.stopPropagation();
                      openEditModal(space);
                    }}
                  >
                    改名
                  </span>
                  {space.id === currentSpace?.id ? (
                    <span className="coz-fg-secondary">✓</span>
                  ) : null}
                </div>
              </Dropdown.Item>
            ))}
            <Dropdown.Divider />
            <Dropdown.Item onClick={openCreateModal}>
              <span className="coz-fg-primary">+ 创建工作空间</span>
            </Dropdown.Item>
          </Dropdown.Menu>
        }
      >
        <div className="cursor-pointer w-full">
          <Space
            className="h-[48px] px-[8px] w-full hover:coz-mg-secondary-hovered rounded-[8px]"
            spacing={8}
          >
            <Avatar
              className="w-[24px] h-[24px] rounded-[6px] shrink-0"
              src={currentSpace?.icon_url}
            />
            <Typography.Text
              ellipsis={{ showTooltip: true, rows: 1 }}
              className="flex-1 coz-fg-primary text-[14px] font-[500]"
            >
              {currentSpace?.name || ''}
            </Typography.Text>
            <Typography.Text className="coz-fg-secondary shrink-0">
              ▾
            </Typography.Text>
          </Space>
        </div>
      </Dropdown>

      <Modal
        title={editingSpaceId ? '编辑工作空间' : '创建工作空间'}
        visible={modalVisible}
        okText={editingSpaceId ? '保存' : '创建'}
        cancelText="取消"
        confirmLoading={submitting}
        onCancel={() => {
          if (!submitting) {
            setModalVisible(false);
          }
        }}
        onOk={handleSubmit}
      >
        <Space vertical spacing={16} className="w-full pt-[8px]">
          <div className="w-full">
            <div className="coz-fg-secondary mb-[8px] text-[13px]">
              工作空间名称
            </div>
            <Input
              value={name}
              maxLength={200}
              showClear
              placeholder="例如：具身机器人和未来脑机接口空间"
              onChange={value => setName(value)}
            />
          </div>
          <div className="w-full">
            <div className="coz-fg-secondary mb-[8px] text-[13px]">
              工作空间描述（可选）
            </div>
            <TextArea
              value={description}
              rows={3}
              maxLength={2000}
              placeholder="这个工作空间用来做什么？"
              onChange={value => setDescription(value)}
            />
          </div>
          {!editingSpaceId ? (
            <div className="coz-fg-secondary text-[12px]">
              每个工作空间的数据完全隔离：智能体、工作流、知识库、长期记忆、生产引擎任务都不会跨空间共享。
            </div>
          ) : null}
        </Space>
      </Modal>
    </>
  );
};
